import { useState, useEffect, useMemo, useCallback, useContext } from 'react'
import { useFormik } from 'formik'
import { getAnswers, getValidations, getVisibility } from './helper'
import { FilledButton, FormCheckbox, FormInput } from 'components'
import { PolicyAnswer, Questions } from 'types/simulators'

import { buildSimulatorForm } from 'utils/simulators'
import {
  Button,
  Card,
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  Label,
  Input,
  Modal,
  ModalContent,
  ModalDescription,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  Spinner,
  toast,
  Upload,
  UploadBody,
  UploadIcon,
  UploadText,
  Textarea,
} from 'keep-react'
import { useNavigate } from 'react-router-dom'
import {
  CheckCircle,
  Download,
  Envelope,
  UploadSimple,
  WarningCircle,
} from 'phosphor-react'
import { downloadPDF } from 'utils/downloadPdf'
import { AuthContext } from 'contexts/AuthContext'
import { deltaToHtmlQuill } from 'utils/quillToHtml'
import { getFirstMessage } from 'utils/normalizeApiErrors'
import { simService } from 'api/services/sim'
import * as Yup from 'yup'
import {
  GetSimulationRatingResponse,
  SaveSimulationResponseBody,
} from 'api/types/sim'
import { docService } from 'api/services/doc'
import {
  GetSimDocsResponseBody,
  SubmitDocumentResponseBody,
  VerifySignatureResponseBody,
} from 'api/types/doc'
import { formatCurrency } from 'utils/currency'
import { RouteNamesAuth } from 'routes/enum'
import { emailService } from 'api/services/email'
import { AppContext } from 'contexts/AppContext'

const sendEmailValidationSchema = Yup.object().shape({
  name: Yup.string().required('Obrigatório'),
  email: Yup.string().email('Email inválido').required('Obrigatório'),
  CCEmail: Yup.string().email('Email inválido'),
  BCCEmail: Yup.string().email('Email inválido'),
})

export const SimulatorsStep2 = ({
  nif,
  holder,
  simulatorData,
  previousValues,
  conditions,
  helper,
  productName,
  productCode,
}: {
  nif: string
  holder: string | PolicyAnswer[]
  simulatorData: Questions | undefined
  previousValues: Record<string, string | boolean> | undefined
  conditions: any
  setCurrentStep: React.Dispatch<React.SetStateAction<number>>
  helper: string
  productName: string
  productCode: string
}) => {
  const { currentBroker } = useContext(AuthContext)
  const { setIsCallingApi } = useContext(AppContext)
  const [conditionsHTML, setConditionsHTML] = useState<string>('')
  const [helperHTML, setHelperHTML] = useState<string>('')
  const { validationSchema, initialValues } = getValidations(
    simulatorData,
    holder as PolicyAnswer[],
    nif,
  )
  const [prize, setPrize] = useState<number | boolean>(false)
  const [answers, setAnswers] = useState('')
  const [visibilityRules, setVisibilityRules] = useState([])
  const policyHolderValue = initialValues.POLICYHOLDER
    ? (initialValues.POLICYHOLDER as string).trim()
    : ''
  const [savedSimulation, setSavedSimulation] = useState<boolean>(false)
  const [PolicyNo, setPolicyNo] = useState<number | null>(null)
  const [EventNo, setEventNo] = useState<number | null>(null)
  const [isCreatingPolicy, setIsCreatingPolicy] = useState(false)
  const [showFileUpload, setShowFileUpload] = useState(false)
  const [saveMessage, setSaveMessage] = useState<string | null>(null)
  const [createBusiness, setCreateBusiness] = useState<boolean>(false)
  const [fullProcess, setFullProcess] = useState<0 | 1>(0)
  const [sendEmailModal, setSendEmailModal] = useState<boolean>(false)
  const [isSendingEmail, setIsSendingEmail] = useState<boolean>(false)
  const navigate = useNavigate()
  const before = `before:inline-block before:align-middle before:text-center before:text-lg before:w-8 before:h-8 before:leading-8 before:rounded-full before:mr-4 before:text-white ${currentBroker?.IsPrimePartner ? 'before:bg-prime' : 'before:bg-primary-600'} before:bg-primary-600`

  useEffect(() => {
    if (simulatorData?.Visibility && simulatorData.Visibility.length > 0) {
      setVisibilityRules(simulatorData.Visibility)
    }
  }, [simulatorData?.Visibility])

  const saveSimulation = async () => {
    if (!answers) return false
    setIsCallingApi(true)
    try {
      const simulationResponse = (await simService.submitSimulation({
        answers: answers,
      })) as SaveSimulationResponseBody
      setSavedSimulation(true)
      setSaveMessage(
        'A sua simulação foi gravada com sucesso. Pode consultar a simulação na página de consultas, onde está guardada com o estado GRAVADO. Pode fazer download da proposta para ser assinada pelo cliente (já pré-preenchida) ou enviá-la por email.',
      )
      setPolicyNo(simulationResponse.PolicyNo)
    } catch (error) {
      toast.error(getFirstMessage(error) || 'Erro ao gravar a simulação.', {
        duration: 12000,
      })
      console.error(error)
    } finally {
      setIsCallingApi(false)
    }
  }

  const createPolicy = async () => {
    if (!answers) return false

    setIsCreatingPolicy(true)

    if (!savedSimulation) {
      await saveSimulation()
    }

    await getSimDocsHandler(1)

    setShowFileUpload(true)
  }

  const onFileUpload = (files: File[]) => {
    if (!files || !PolicyNo) return

    const reader = new FileReader()
    reader.onload = async () => {
      const base64 = reader.result
      if (base64 && typeof base64 === 'string') {
        try {
          const submitDocumentResponse = (await docService.submitDocument({
            policyNo: Number(PolicyNo)!,
            base64String: base64.replace(/^data:application\/pdf;base64,/, ''),
            productCode: productCode,
          })) as SubmitDocumentResponseBody

          if (submitDocumentResponse.Status.OK) {
            toast.success('Documento submetido com sucesso')

            setShowFileUpload(false)

            try {
              const verifySignatureResponse = (await docService.verifySignature(
                {
                  policyNo: Number(PolicyNo)!,
                  base64String: base64.replace(
                    /^data:application\/pdf;base64,/,
                    '',
                  ),
                },
              )) as VerifySignatureResponseBody

              if (verifySignatureResponse.status) {
                setFullProcess(1)
                try {
                  await simService.logSimulationInfo({
                    simCode: simulatorData?.ProductCode || '',
                    eventType: 'PORTAL',
                    observation: String(PolicyNo!),
                  })
                  ;(await simService.createNewBusiness({
                    policyNo: Number(PolicyNo!),
                    fullProcess: 1,
                    brokerNo: String(currentBroker?.BrokerNo),
                  })) as any

                  setShowFileUpload(false)
                  setCreateBusiness(true)
                } catch (e) {
                  toast.error(
                    'Erro a emitir a apólice. Contacte a Innovarisk.',
                    {
                      duration: 12000,
                    },
                  )
                }
              } else {
                setFullProcess(0)
                toast.error(
                  'Assinatura não validada. Não se preocupe! A sua assinatura será agora validada pela equipa Innovarisk.',
                  {
                    duration: 12000,
                  },
                )
              }
            } catch (e) {
              toast.error('Erro na verificação da assinatura.', {
                duration: 12000,
              })
            }
          } else {
            toast.error('Erro ao submeter o documento.', {
              duration: 12000,
            })
          }
        } catch (error) {
          toast.error('Erro ao submeter o documento.', {
            duration: 12000,
          })
        }
      }
    }
    reader.onerror = error => {
      console.error('Error reading file:', error)
    }

    if (files[0].type !== 'application/pdf') {
      toast.error('Por favor faça upload de um ficheiro PDF.')
      return
    }
    reader.readAsDataURL(files[0])
  }

  const formikStep2 = useFormik({
    initialValues:
      previousValues && Object.keys(previousValues).length !== 0
        ? previousValues
        : initialValues,
    validateOnBlur: false,
    validateOnChange: false,
    validationSchema,
    onSubmit: async values => {
      const ans = getAnswers(simulatorData, currentBroker?.BrokerNo!, values)
      setAnswers(ans)
      setIsCallingApi(true)
      try {
        const response = (await simService.getSimulationRating({
          answers: ans,
        })) as GetSimulationRatingResponse

        if (response.Status.OK !== true && response.Status.Errors) {
          toast.error(response.Status.Errors[0].ErrorText as string)
        } else {
          setPrize(
            response.Policy.Events[0].Transaction.AnnualPremium +
              response.Policy.Events[0].Transaction.AnnualTax,
          )
          setEventNo(response.Policy.Events[0].EventNo)
          setSavedSimulation(false)
        }
      } catch (error) {
        toast.error(getFirstMessage(error))
        console.error(error)
      } finally {
        setIsCallingApi(false)
      }
    },
  })

  const sendEmailFormik = useFormik({
    initialValues: {
      name: '',
      email: '',
      CCEmail: '',
      BCCEmail: '',
      EmailBody: '',
    },
    validateOnBlur: false,
    validateOnChange: false,
    validationSchema: sendEmailValidationSchema,
    onSubmit: async values => {
      setIsSendingEmail(true)
      setIsCallingApi(true)
      const body = {
        email: values.email,
        CCEmail: values.CCEmail,
        BCCEmail: values.BCCEmail,
        EmailBody: values.EmailBody,
        username: values.name,
        policyNo: String(PolicyNo),
        broker: currentBroker?.BrokerName || '',
        eventNo: String(EventNo) || '',
        product: productName || '',
      }

      try {
        await emailService.sendProposalEmail(body)
        toast.success('Email enviado com sucesso.')
        setSendEmailModal(false)
      } catch (error) {
        toast.error('Erro ao enviar o email.')
        console.error('Error sending email:', error)
      } finally {
        setIsSendingEmail(false)
        setIsCallingApi(false)
      }
    },
  })

  const personalData = useMemo(() => {
    let newData = simulatorData?.Policyholder
    let tempLocal = newData && newData[5]
    simulatorData && newData
      ? (newData[5] = simulatorData.Policyholder[6])
      : undefined
    newData && tempLocal ? (newData[6] = tempLocal) : undefined

    return newData
  }, [simulatorData?.Policyholder])

  const isVisible = useCallback(
    (InputId: any, visibilityRules: any, formikStep2: any) =>
      getVisibility(InputId, visibilityRules, formikStep2),
    [],
  )

  const getSimDocsHandler = async (index: number) => {
    if (!EventNo || !PolicyNo) return
    setIsCallingApi(true)
    try {
      const response = (await docService.getSimDocs(
        Number(PolicyNo),
        EventNo,
      )) as GetSimDocsResponseBody

      downloadPDF({
        base64Data: response.Documents[index].Document,
        filename: `${index === 0 ? 'Cotação' : 'Proposta'} - ${PolicyNo}.pdf`,
      })
    } catch (error) {
      console.error('Error fetching document:', error)
    } finally {
      setIsCallingApi(false)
    }
  }

  useEffect(() => {
    if (!conditions) return
    const html = deltaToHtmlQuill(conditions)
    setConditionsHTML(html)
  }, [conditions])

  useEffect(() => {
    if (!helper) return
    const html = deltaToHtmlQuill(helper)
    setHelperHTML(html)
  }, [helper])

  return (
    <div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
        <Card className="w-full max-w-none p-4">
          <h3
            className={`text-body-1 py-4 before:content-['2'] px-4 bg-metal-100 ${before}`}
          >
            Capitais Seguros
          </h3>
          <form
            className="flex flex-col pt-10"
            onSubmit={formikStep2.handleSubmit}
          >
            <h4 className="text-body-1 uppercase pb-4">
              Identificação do Tomador
            </h4>
            <div className="flex flex-col gap-4">
              {personalData?.map(item => {
                return !(
                  item.InputId === 'POLICYHOLDERID' ||
                  item.InputId === 'COUNTRY'
                ) ? (
                  <FormInput
                    key={item.InputId}
                    type="text"
                    name={item.InputId}
                    label={item.QuestionText ?? ''}
                    formik={formikStep2}
                    placeholder={item.InputId === 'POSTCODE' ? '####-###' : ''}
                    disabled={Boolean(
                      item.InputId === 'POLICYHOLDER' && policyHolderValue,
                    )}
                    maxLength={50}
                  />
                ) : null
              })}
            </div>
            {simulatorData?.Risks &&
              simulatorData.Risks.map((risk, indexr) => {
                return (
                  <div key={`risk${indexr}`}>
                    {risk.Covers.map((cover, index1) => {
                      return (
                        <div className="pt-10" key={`cover${index1}`}>
                          <h4 className="text-body-1 uppercase pb-4">
                            {cover.Description}
                          </h4>
                          <div className="flex flex-col">
                            {cover.Items.map(item => {
                              return item.Questions.map(question => {
                                return (
                                  <div key={question.InputId}>
                                    {buildSimulatorForm(
                                      question.Attribute,
                                      question,
                                      `${
                                        isVisible(
                                          question.InputId,
                                          visibilityRules,
                                          formikStep2.values,
                                        )
                                          ? 'hidden'
                                          : 'block'
                                      } py-2`,
                                      formikStep2,
                                    )}
                                  </div>
                                )
                              })
                            })}
                          </div>

                          <div className="flex flex-col">
                            {cover.Questions.map((question, index3) => {
                              return (
                                <div key={`cover${index1}_question${index3}`}>
                                  {buildSimulatorForm(
                                    question.Attribute,
                                    question,
                                    `${
                                      isVisible(
                                        question.InputId,
                                        visibilityRules,
                                        formikStep2.values,
                                      )
                                        ? 'hidden'
                                        : 'block'
                                    } py-2`,
                                    formikStep2,
                                  )}
                                </div>
                              )
                            })}
                          </div>
                        </div>
                      )
                    })}

                    {risk && risk.Questions.length > 0 && (
                      <div className="pt-10">
                        <h4 className="text-body-1 uppercase pb-4">
                          Outras Questões
                        </h4>
                      </div>
                    )}

                    <div className="flex flex-col">
                      {risk &&
                        risk.Questions.length > 0 &&
                        risk.Questions.map((question, index) => {
                          return (
                            <div key={`question1${index}`}>
                              {buildSimulatorForm(
                                question.Attribute,
                                question,
                                `${
                                  isVisible(
                                    question.InputId,
                                    visibilityRules,
                                    formikStep2.values,
                                  )
                                    ? 'hidden'
                                    : 'block'
                                } py-2`,
                                formikStep2,
                              )}
                            </div>
                          )
                        })}
                    </div>
                  </div>
                )
              })}

            <div className="pt-10">
              <div className="flex flex-col">
                {simulatorData &&
                  simulatorData.Questions.length > 0 &&
                  simulatorData.Questions.map((question, index) => {
                    return (
                      <div key={`question2${index}`}>
                        {buildSimulatorForm(
                          question.Attribute,
                          question,
                          `${
                            isVisible(
                              question.InputId,
                              visibilityRules,
                              formikStep2.values,
                            )
                              ? 'hidden'
                              : 'block'
                          } py-2`,
                          formikStep2,
                        )}
                      </div>
                    )
                  })}
              </div>
            </div>

            <div>
              <h3
                className={`font-medium text-body-4 ${currentBroker?.IsPrimePartner ? 'text-prime' : 'text-primary-600'} pt-10 pb-4`}
              >
                CONDIÇÕES DA COTAÇÃO
              </h3>
              <div
                dangerouslySetInnerHTML={{ __html: conditionsHTML }}
                className="[&_h2]:text-xxl [&_h3]:text-xl [&_h4]:text-lg [&_h2]:py-4 [&_h3]:py-3 [&_h4]:py-2 [&_p]:text-body-4 [&_p]:pb-4 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:pb-4 [&_ul]:text-body-4 [&_ol]:list-disc [&_ol]:pl-6 [&_ol]:pb-4 [&_ol]:text-body-4 [&_a]:text-primary-600 [&_a]:underline"
              ></div>
              <FormCheckbox
                name="acceptance"
                label="Li e aceito as condições da cotação."
                formik={formikStep2}
                prime={currentBroker?.IsPrimePartner}
              />
              <input
                value={formikStep2.getFieldProps('COUNTRY').value}
                className="hidden"
              />
            </div>

            <div className="flex flex-col border-t-2 py-6 mt-10 gap-y-6">
              <div className="flex items-center justify-end gap-x-6">
                <FilledButton
                  label="Simular"
                  type="submit"
                  onClick={e => {
                    e.preventDefault()
                  }}
                  className={`h-11 ${currentBroker?.IsPrimePartner ? 'bg-prime hover:bg-black' : 'bg-primary-600'}`}
                />
              </div>
              {Object.keys(formikStep2.errors).length > 0 && (
                <p className="py-2 block w-full text-body-5 text-error">
                  Por favor corrija os campos assinalados
                </p>
              )}
            </div>
          </form>
        </Card>

        <div>
          <Card className="w-full max-w-none p-4 h-auto">
            <div
              dangerouslySetInnerHTML={{ __html: helperHTML }}
              className="[&_h2]:text-xxl [&_h3]:text-xl [&_h4]:text-lg [&_h2]:py-4 [&_h3]:py-3 [&_h4]:py-2 [&_p]:text-body-4 [&_p]:pb-4 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:pb-4 [&_ul]:text-body-4 [&_ol]:list-disc [&_ol]:pl-6 [&_ol]:pb-4 [&_ol]:text-body-4 [&_a]:text-primary-600 [&_a]:underline"
            ></div>
          </Card>
        </div>

        <Drawer
          open={prize ? true : false}
          showCloseIcon={false}
          onOpenChange={() => setPrize(false)}
        >
          <DrawerContent position="right" className="bg-metal-25 xl:w-1/3">
            <DrawerHeader className="space-y-10 pb-10">
              <DrawerTitle className="text-body-1 text-primary-500 uppercase tracking-wider pb-2">
                RESULTADO DA SIMULAÇÃO
              </DrawerTitle>
            </DrawerHeader>
            <div>
              <div className="flex items-center justify-between pb-4 border-b border-metal-200 mb-10">
                <h3 className="text-body-2 font-light uppercase tracking-wider">
                  Prémio Total
                </h3>
                <h3 className="text-body-2 font-bold">
                  {formatCurrency(prize ? (prize as number) : 0)}
                </h3>
              </div>

              <nav className="grid grid-cols-3 items-center gap-4 w-full">
                <Button
                  color="secondary"
                  variant="softBg"
                  type="button"
                  onClick={() => {
                    setPrize(false)
                  }}
                >
                  Simular Novamente
                </Button>
                <Button
                  color="secondary"
                  variant="softBg"
                  type="button"
                  onClick={() => {
                    saveSimulation()
                  }}
                  disabled={savedSimulation}
                >
                  Gravar Simulação
                </Button>
                <Button
                  color="primary"
                  variant="softBg"
                  type="button"
                  disabled={isCreatingPolicy}
                  onClick={() => {
                    createPolicy()
                  }}
                >
                  Emitir Apólice
                </Button>
              </nav>
              {savedSimulation && (
                <div className="space-y-4 my-10 border-y border-metal-200 py-10">
                  <h3 className="text-body-1 text-primary-500 uppercase">
                    A SUA COTAÇÃO
                  </h3>
                  {saveMessage && (
                    <div className="p-4 bg-metal-800 rounded-lg border border-metal-100">
                      <p className="text-body-3 text-white">{saveMessage}</p>
                    </div>
                  )}
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-2">
                      <Button
                        color="secondary"
                        variant="softBg"
                        className="gap-2 w-full"
                        onClick={() =>
                          navigate(
                            RouteNamesAuth.QUOTATION_DETAILS.replace(
                              ':policyNo',
                              `${PolicyNo}`,
                            ),
                          )
                        }
                      >
                        Ver Cotação
                      </Button>
                      <Button
                        color="secondary"
                        variant="softBg"
                        className="gap-2 w-full"
                        onClick={() => getSimDocsHandler(0)}
                      >
                        <Download size={16} />
                        Download da Cotação
                      </Button>

                      <Button
                        color="secondary"
                        variant="softBg"
                        className="gap-2 w-full"
                        onClick={() => getSimDocsHandler(1)}
                      >
                        <Download size={16} />
                        Download da Proposta
                      </Button>
                      <Button
                        color="secondary"
                        variant="softBg"
                        className="gap-2 w-full"
                        onClick={() => setSendEmailModal(true)}
                      >
                        <Envelope size={16} />
                        Enviar proposta por email
                      </Button>
                    </div>

                    <nav className="flex items-center gap-4 w-full flex-wrap"></nav>
                  </div>
                </div>
              )}
              <div>
                {showFileUpload && PolicyNo && (
                  <>
                    <h3 className="text-body-1 text-primary-500 uppercase">
                      UPLOAD DOCUMENTO
                    </h3>
                    <div className="py-4">
                      <Upload
                        horizontal
                        options={{ onDrop: onFileUpload, multiple: false }}
                      >
                        <UploadBody>
                          <UploadIcon>
                            <UploadSimple size={48} />
                          </UploadIcon>
                          <UploadText>
                            <p className="text-body-3 font-medium text-metal-600">
                              Por favor faça upload da proposta de seguro
                              assinada pelo Tomador para concluir a emissão da
                              apólice.
                            </p>
                            <p className="text-body-4 font-normal text-metal-400">
                              Apenas 1 ficheiro PDF
                            </p>
                          </UploadText>
                        </UploadBody>
                      </Upload>
                    </div>
                  </>
                )}

                {createBusiness && (
                  <div className="space-y-4">
                    <h3 className="text-body-1 text-primary-500 uppercase ">
                      Emissão
                    </h3>
                    <div className="p-4 bg-metal-800 rounded-lg border border-metal-100 grid grid-cols-[24px_1fr] gap-4">
                      {fullProcess === 1 && (
                        <CheckCircle size={24} className="text-success-500" />
                      )}
                      {fullProcess === 0 && (
                        <WarningCircle size={24} className="text-warning-500" />
                      )}
                      <div>
                        <p className="text-body-3 text-white font-bold pb-2">
                          {fullProcess === 1
                            ? 'Assinatura validada com sucesso'
                            : 'Assinatura não validada'}
                        </p>
                        <p className="text-body-3 text-white">
                          {fullProcess === 1
                            ? 'Obrigado por contratar com a Innovarisk. Receberá os dados para pagamento do seu seguro no seu email em 24 horas.'
                            : 'Não se preocupe. A sua assinatura será agora validada pela equipa Innovarisk.'}
                        </p>
                      </div>
                    </div>

                    <div className="mt-4">
                      {fullProcess === 1 && (
                        <Button
                          color="primary"
                          variant="softBg"
                          onClick={() => {
                            navigate(RouteNamesAuth.POLICIES)
                          }}
                        >
                          Ver Apólices
                        </Button>
                      )}
                      {fullProcess === 0 && (
                        <Button
                          color="primary"
                          variant="softBg"
                          onClick={() => {
                            navigate(RouteNamesAuth.QUOTATIONS)
                          }}
                        >
                          Ver Cotações
                        </Button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </DrawerContent>
        </Drawer>

        {/*  Modal to send quotation by email */}
        <Modal
          open={sendEmailModal}
          onOpenChange={() => setSendEmailModal(false)}
        >
          <ModalContent>
            <ModalHeader className="mb-6 space-y-3">
              <ModalTitle>Enviar Cotação por Email</ModalTitle>
              <ModalDescription className="hidden">
                Modal para enviar proposta
              </ModalDescription>
              <form
                onSubmit={sendEmailFormik.handleSubmit}
                className="space-y-4"
              >
                <div>
                  <Label htmlFor="name">Nome</Label>
                  <Input
                    id="name"
                    type="text"
                    name="name"
                    placeholder="nome"
                    onChange={sendEmailFormik.handleChange}
                    className={
                      sendEmailFormik.errors.name ? 'border-error-500' : ''
                    }
                  />
                </div>
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    name="email"
                    placeholder="email"
                    onChange={sendEmailFormik.handleChange}
                    className={
                      sendEmailFormik.errors.email ? 'border-error-500' : ''
                    }
                  />
                </div>
                <div>
                  <Label htmlFor="CCEmail">CC Email</Label>
                  <Input
                    id="CCEmail"
                    type="email"
                    name="CCEmail"
                    placeholder="CC email"
                    onChange={sendEmailFormik.handleChange}
                    className={
                      sendEmailFormik.errors.CCEmail ? 'border-error-500' : ''
                    }
                  />
                </div>
                <div>
                  <Label htmlFor="BCCEmail">BCC Email</Label>
                  <Input
                    id="CCEmail"
                    type="email"
                    name="BCCEmail"
                    placeholder="BCC email"
                    onChange={sendEmailFormik.handleChange}
                    className={
                      sendEmailFormik.errors.BCCEmail ? 'border-error-500' : ''
                    }
                  />
                </div>
                <div>
                  <Label htmlFor="EmailBody">Corpo do Email</Label>
                  <Textarea
                    id="EmailBody"
                    name="EmailBody"
                    placeholder="Corpo do email"
                    onChange={sendEmailFormik.handleChange}
                    className={
                      sendEmailFormik.errors.EmailBody ? 'border-error-500' : ''
                    }
                  />
                </div>
              </form>
            </ModalHeader>
            <ModalFooter className="grid grid-cols-2 gap-2">
              <Button
                variant="outline"
                color="secondary"
                onClick={() => setSendEmailModal(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                onClick={() => sendEmailFormik.handleSubmit()}
                disabled={isSendingEmail}
              >
                {isSendingEmail ? <Spinner /> : 'Enviar'}
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </div>
    </div>
  )
}
